import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdMoreHoriz } from 'react-icons/md';

// Actions d'une ligne du tableau des leads : toutes regroupées dans un menu « ⋯ ». Le menu est rendu dans un portail en position fixe : le conteneur
// overflow-x-auto du tableau le couperait sinon.

export type RowActionTone = 'primary' | 'success' | 'warning' | 'purple' | 'danger' | 'neutral';

export interface RowAction {
    key: string;
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    title?: string;
    tone?: RowActionTone;
    disabled?: boolean;
    loading?: boolean;
}

const MENU_TONES: Record<RowActionTone, string> = {
    primary: 'text-gray-700 dark:text-gray-200',
    success: 'text-green-700 dark:text-green-400',
    warning: 'text-amber-700 dark:text-amber-400',
    purple: 'text-purple-700 dark:text-purple-400',
    danger: 'text-red-600 dark:text-red-400',
    neutral: 'text-gray-700 dark:text-gray-200',
};

const MENU_WIDTH = 224;

const RowActions = ({ actions }: { actions: RowAction[] }) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const estimatedHeight = actions.length * 34 + 8;
        const openUp = rect.bottom + estimatedHeight > window.innerHeight - 8 && rect.top > estimatedHeight;
        setPosition({
            top: openUp ? rect.top - 4 : rect.bottom + 4,
            left: Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
            openUp,
        });
    }, [open, actions.length]);

    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
            close();
        };
        const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && close();
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    return (
        <div className="flex items-center justify-end">
            {actions.length > 0 && (
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    title="Plus d'actions"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    className={`inline-flex h-7 w-7 items-center justify-center rounded-md border text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 dark:border-navy-600 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white ${open ? 'border-gray-300 bg-gray-100 text-gray-800 dark:bg-navy-700' : 'border-gray-200'}`}
                >
                    <MdMoreHoriz size={18} />
                </button>
            )}
            {open && position && createPortal(
                <div
                    ref={menuRef}
                    role="menu"
                    style={{ position: 'fixed', top: position.top, left: position.left, width: MENU_WIDTH, transform: position.openUp ? 'translateY(-100%)' : undefined }}
                    className="z-[1000] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-navy-600 dark:bg-navy-800"
                >
                    {actions.map((action, idx) => {
                        const isDanger = action.tone === 'danger';
                        const separated = isDanger && idx > 0 && actions[idx - 1].tone !== 'danger';
                        return (
                            <React.Fragment key={action.key}>
                                {separated && <div className="my-1 border-t border-gray-100 dark:border-navy-700" />}
                                <button
                                    type="button"
                                    role="menuitem"
                                    title={action.title}
                                    disabled={action.disabled || action.loading}
                                    onClick={() => {
                                        setOpen(false);
                                        action.onClick();
                                    }}
                                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-medium transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-navy-700 ${MENU_TONES[action.tone || 'neutral']}`}
                                >
                                    <span className="flex w-4 shrink-0 justify-center">{action.icon}</span>
                                    {action.loading ? '…' : action.label}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>,
                document.body
            )}
        </div>
    );
};

export default RowActions;
